import { useState, useRef } from 'react'
import { Search, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { NotebookNote } from '../../../shared/types/notebook'
import { searchNotes } from '../notebookTree'
import { appendMeetingLinkToContent } from '../meetingLink'
import { Dialog, DialogContent, DialogTitle } from '@tendbot/ui'

interface Props {
  open: boolean
  onClose: () => void
  notes: NotebookNote[]
  meetingId: string
  meetingTitle: string
  onUpdateNote: (noteId: string, updates: Partial<NotebookNote>) => void
}

export default function AddMeetingLinkSheet({
  open,
  onClose,
  notes,
  meetingId,
  meetingTitle,
  onUpdateNote,
}: Props) {
  const { t, i18n } = useTranslation('notebook')
  const [query, setQuery] = useState('')
  const [linkedId, setLinkedId] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleClose = () => {
    setQuery('')
    setLinkedId(null)
    onClose()
  }

  const handleSelect = (target: NotebookNote) => {
    const next = appendMeetingLinkToContent(target.contentMd, meetingId, meetingTitle, i18n.language)
    if (next !== target.contentMd) {
      onUpdateNote(target.id, { contentMd: next })
    }
    setLinkedId(target.id)
    setTimeout(handleClose, 700)
  }

  // Exclude the meeting itself and pending/meetings-root/synthetic entries
  const eligible = notes.filter(n =>
    n.id !== meetingId &&
    !n.id.startsWith('pending-') &&
    !n.tags.includes('_pending') &&
    !n.tags.includes('_meetings_root'),
  )
  const results = query ? searchNotes(eligible, query) : eligible.slice(0, 20)

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose() }}>
      <DialogContent
        showCloseButton={false}
        className="max-h-[70vh] p-0 overflow-hidden flex flex-col"
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          inputRef.current?.focus()
        }}
      >
        <DialogTitle className="sr-only">{t('addToPage.searchPlaceholder')}</DialogTitle>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gold/10">
          <Search size={16} className="text-pearl/60 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('addToPage.searchPlaceholder')}
            className="flex-1 bg-transparent text-ivory text-sm placeholder:text-pearl/55 focus:outline-none"
          />
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {results.length === 0 && (
            <p className="text-pearl/60 text-sm text-center py-8">{t('noResults')}</p>
          )}
          {results.map(note => {
            const alreadyLinked = note.contentMd.includes(`[[${meetingId}]]`)
            const justLinked = linkedId === note.id
            return (
              <button
                key={note.id}
                onClick={() => handleSelect(note)}
                disabled={alreadyLinked || linkedId !== null}
                className="flex items-center gap-2.5 w-full px-4 py-2.5 text-left hover:bg-white/[0.03] transition-colors disabled:opacity-50 disabled:hover:bg-transparent"
              >
                <span className="text-base flex-shrink-0">{note.icon}</span>
                <span className="text-ivory text-sm truncate flex-1">{note.title || t('page.untitledPlaceholder')}</span>
                {(alreadyLinked || justLinked) && (
                  <Check size={14} className="text-emerald flex-shrink-0" />
                )}
              </button>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}
